import {
  ArrowRight,
  ExternalLink,
  MapPin,
  Pencil,
  Plus,
  Star,
  Trash2,
} from "lucide-react";
import {
  useCallback,
  startTransition,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { Breakdown, EstimateRequest } from "../../../lib/agent-types";
import { fetchCashToClosePreview } from "../../../lib/cash-to-close";
import type { TrackedProperty } from "../../../lib/dashboard";
import type { DashboardSnapshot } from "../../../lib/dashboard";
import {
  buildDashboardSnapshot,
  formatCompactCurrency,
} from "../../../lib/dashboard";
import {
  dollarsToCents,
  formatCurrency,
  formatPercent,
  percentToDecimal,
} from "../../../lib/financials";
import { getApiErrorMessage } from "../../../lib/onboarding";
import { getSavedPropertyVisualTokenClasses } from "../../../lib/property-visual-tokens";
import {
  createSavedProperty,
  deleteSavedProperty,
  formatSavedPropertyType,
  getActiveReadinessTarget,
  listSavedProperties,
  previewSavedPropertiesReadiness,
  SAVED_PROPERTY_STATUS_OPTIONS,
  setSavedPropertyAsReadinessTarget,
  type ListSavedPropertiesFilters,
  type SavedPropertyReadinessPreview,
  type SavedProperty,
  type SavedPropertyStatus,
  updateSavedProperty,
} from "../../../lib/properties";
import { getReadinessScoreAppearance } from "../../../lib/readiness-score-appearance";
import type { ReadinessTarget } from "../../../lib/readiness-types";
import { cn } from "../../../lib/utils";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Dialog, DialogContent } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { PageHeader } from "../../ui/page-header";
import { Select } from "../../ui/select";
import { PropertiesListSkeleton } from "../../ui/skeleton";
import { useToast } from "../../ui/toast";
import { PropertyFormFields } from "./property-form";
import {
  buildSavedPropertyInput,
  buildSavedPropertyInputFromProperty,
  emptyFormState,
  formStateFromProperty,
  validateFormState,
  type PropertyFormState,
} from "./property-form.helpers";
import { resolvePropertyFit } from "./properties-view.helpers";

export type PropertiesViewIntent =
  | { kind: "create" }
  | { kind: "edit"; propertyID: string }
  | null;

type DialogState =
  | { kind: "create" }
  | { kind: "delete"; propertyID: string }
  | { kind: "edit"; propertyID: string }
  | { kind: "target"; propertyID: string }
  | null;

type InlineActionState = {
  kind: "favorite" | "status" | "target";
  propertyID: string;
} | null;

type ReadinessPreviewState = Record<
  string,
  SavedPropertyReadinessPreview | null
>;
type CashPreviewState = Record<string, Breakdown | null | undefined>;

const STATUS_FILTER_ITEMS = [
  { label: "All statuses", value: "all" },
  ...SAVED_PROPERTY_STATUS_OPTIONS,
];

const DEFAULT_RATE_PERCENT = 6.75;
const PREVIEW_CLOSING_COST_RATE = 0.03;
const PREVIEW_INSURANCE_RATE = 0.0042;
const PREVIEW_PMI_RATE = 0.0062;
const PREVIEW_PROPERTY_TAX_RATE = 0.0108;

export function PropertiesView({
  getToken,
  intent = null,
  isLoaded,
  isSignedIn,
  onIntentHandled,
  onOpenProperty,
  onReadinessChanged,
  onTrackedPropertiesChange,
  snapshot: snapshotProp,
}: {
  getToken: () => Promise<string | null>;
  intent?: PropertiesViewIntent;
  isLoaded: boolean;
  isSignedIn: boolean;
  onIntentHandled?: () => void;
  onOpenProperty?: (propertyID: string) => void;
  onReadinessChanged?: () => void;
  onTrackedPropertiesChange?: (trackedProperties: TrackedProperty[]) => void;
  snapshot?: DashboardSnapshot;
}) {
  const { pushToast } = useToast();
  const fallbackSnapshot = useMemo(() => buildDashboardSnapshot(), []);
  const snapshot = snapshotProp ?? fallbackSnapshot;
  const snapshotRef = useRef(snapshot);
  const cashPreviewSnapshotKey = buildCashPreviewSnapshotKey(snapshot);
  const [dialogState, setDialogState] = useState<DialogState>(null);
  const [favoriteOnly, setFavoriteOnly] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formState, setFormState] = useState<PropertyFormState>(() =>
    emptyFormState(),
  );
  const [activeTarget, setActiveTarget] = useState<ReadinessTarget | null>(
    null,
  );
  const cashPreviewTargetKey = buildCashPreviewTargetKey(activeTarget);
  const cashPreviewInputKey = `${cashPreviewSnapshotKey}|${cashPreviewTargetKey}`;
  const lastCashPreviewInputKeyRef = useRef<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState<SavedProperty[]>([]);
  const [cashPreviews, setCashPreviews] = useState<CashPreviewState>({});
  const [query, setQuery] = useState("");
  const [readinessPreviews, setReadinessPreviews] =
    useState<ReadinessPreviewState>({});
  const [inlineAction, setInlineAction] = useState<InlineActionState>(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [targetCandidate, setTargetCandidate] = useState<SavedProperty | null>(
    null,
  );
  const deferredQuery = useDeferredValue(query);
  const handledIntentRef = useRef<string | null>(null);
  const onIntentHandledRef = useRef(onIntentHandled);
  const onTrackedPropertiesChangeRef = useRef(onTrackedPropertiesChange);

  const selectedProperty = useMemo(() => {
    if (!dialogState || dialogState.kind === "create") {
      return null;
    }
    if (
      dialogState.kind === "target" &&
      targetCandidate?.id === dialogState.propertyID
    ) {
      return targetCandidate;
    }
    return (
      properties.find((property) => property.id === dialogState.propertyID) ??
      null
    );
  }, [dialogState, properties, targetCandidate]);
  const activePropertyID =
    activeTarget?.kind === "property"
      ? (activeTarget.property_id ?? null)
      : null;
  const hasActivePropertyFilters =
    Boolean(query) || favoriteOnly || statusFilter !== "all";
  const shouldShowPropertyFilters =
    loading ||
    Boolean(loadError) ||
    properties.length > 0 ||
    hasActivePropertyFilters;

  const loadReadinessPreviews = useCallback(
    async (
      nextProperties: SavedProperty[],
      token: string,
      nextActiveTarget: ReadinessTarget | null,
      isCancelled: () => boolean = () => false,
    ) => {
      const nextReadinessPreviews = await previewSavedPropertiesReadiness(
        nextProperties,
        token,
        nextActiveTarget,
      );

      if (isCancelled()) {
        return;
      }

      startTransition(() => {
        setReadinessPreviews(nextReadinessPreviews);
      });
      onTrackedPropertiesChangeRef.current?.(
        nextProperties
          .slice(0, 3)
          .map((property) =>
            toTrackedProperty(property, nextReadinessPreviews[property.id]),
          ),
      );
    },
    [],
  );

  const loadCashPreviews = useCallback(
    async (
      nextProperties: SavedProperty[],
      token: string,
      nextActiveTarget: ReadinessTarget | null,
      snapshotForPreview: DashboardSnapshot,
      isCancelled: () => boolean = () => false,
    ) => {
      const entries = await Promise.all(
        nextProperties.map(async (property) => {
          const request = buildPropertyCashEstimateRequest(
            property,
            snapshotForPreview,
            nextActiveTarget,
          );
          try {
            const response = await fetchCashToClosePreview(
              buildPropertyCashRequest(request, nextActiveTarget),
              { token },
            );
            return [property.id, response.breakdown] as const;
          } catch {
            return [property.id, null] as const;
          }
        }),
      );

      if (isCancelled()) {
        return;
      }

      startTransition(() => {
        setCashPreviews(Object.fromEntries(entries));
      });
    },
    [],
  );

  useEffect(() => {
    onIntentHandledRef.current = onIntentHandled;
  }, [onIntentHandled]);

  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  useEffect(() => {
    onTrackedPropertiesChangeRef.current = onTrackedPropertiesChange;
  }, [onTrackedPropertiesChange]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      return;
    }

    let cancelled = false;
    const filters: ListSavedPropertiesFilters = {
      favoriteOnly,
      q: deferredQuery || undefined,
      status:
        statusFilter === "all"
          ? undefined
          : (statusFilter as SavedPropertyStatus),
    };

    void (async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const token = await getToken();
        if (!token) {
          throw new Error("Missing auth token");
        }

        const [result, nextActiveTarget] = await Promise.all([
          listSavedProperties(filters, token),
          getActiveReadinessTarget(token),
        ]);
        if (cancelled) {
          return;
        }

        startTransition(() => {
          setActiveTarget(nextActiveTarget);
          setProperties(result.properties);
          setLoading(false);
        });
        onTrackedPropertiesChangeRef.current?.(
          result.properties
            .slice(0, 3)
            .map((property) => toTrackedProperty(property)),
        );
        void loadReadinessPreviews(
          result.properties,
          token,
          nextActiveTarget,
          () => cancelled,
        );
        const previewSnapshot = snapshotRef.current;
        lastCashPreviewInputKeyRef.current = buildCashPreviewInputKey(
          previewSnapshot,
          nextActiveTarget,
        );
        void loadCashPreviews(
          result.properties,
          token,
          nextActiveTarget,
          previewSnapshot,
          () => cancelled,
        );
      } catch (error) {
        if (cancelled) {
          return;
        }
        setLoadError(
          error instanceof Error ? error.message : "Unable to load properties.",
        );
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    deferredQuery,
    favoriteOnly,
    getToken,
    isLoaded,
    isSignedIn,
    loadCashPreviews,
    loadReadinessPreviews,
    cashPreviewSnapshotKey,
    statusFilter,
  ]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || loading || properties.length === 0) {
      return;
    }
    if (lastCashPreviewInputKeyRef.current === cashPreviewInputKey) {
      return;
    }

    let cancelled = false;
    lastCashPreviewInputKeyRef.current = cashPreviewInputKey;

    void (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) {
          return;
        }
        void loadCashPreviews(
          properties,
          token,
          activeTarget,
          snapshotRef.current,
          () => cancelled,
        );
      } catch {
        if (!cancelled) {
          lastCashPreviewInputKeyRef.current = null;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeTarget,
    cashPreviewInputKey,
    getToken,
    isLoaded,
    isSignedIn,
    loadCashPreviews,
    loading,
    properties,
  ]);

  useEffect(() => {
    if (!intent) {
      handledIntentRef.current = null;
      return;
    }

    const intentKey =
      intent.kind === "create" ? "create" : `edit:${intent.propertyID}`;
    if (handledIntentRef.current === intentKey) {
      return;
    }

    if (intent.kind === "create") {
      handledIntentRef.current = intentKey;
      startTransition(() => {
        setFormState(emptyFormState());
        setFormError(null);
        setDialogState({ kind: "create" });
      });
      onIntentHandledRef.current?.();
      return;
    }

    const property = properties.find((item) => item.id === intent.propertyID);
    if (!property) {
      return;
    }
    handledIntentRef.current = intentKey;
    startTransition(() => {
      setFormState(formStateFromProperty(property));
      setFormError(null);
      setDialogState({ kind: "edit", propertyID: property.id });
    });
    onIntentHandledRef.current?.();
  }, [intent, properties]);

  const refreshProperties = async () => {
    const token = await getToken();
    if (!token) {
      throw new Error("Missing auth token");
    }
    const [result, nextActiveTarget] = await Promise.all([
      listSavedProperties(
        {
          favoriteOnly,
          q: deferredQuery || undefined,
          status:
            statusFilter === "all"
              ? undefined
              : (statusFilter as SavedPropertyStatus),
        },
        token,
      ),
      getActiveReadinessTarget(token),
    ]);
    startTransition(() => {
      setActiveTarget(nextActiveTarget);
      setProperties(result.properties);
    });
    onTrackedPropertiesChangeRef.current?.(
      result.properties
        .slice(0, 3)
        .map((property) => toTrackedProperty(property)),
    );
    void loadReadinessPreviews(result.properties, token, nextActiveTarget);
    const previewSnapshot = snapshotRef.current;
    lastCashPreviewInputKeyRef.current = buildCashPreviewInputKey(
      previewSnapshot,
      nextActiveTarget,
    );
    void loadCashPreviews(
      result.properties,
      token,
      nextActiveTarget,
      previewSnapshot,
    );
    return result.properties;
  };

  const openCreateDialog = () => {
    setFormState(emptyFormState());
    setFormError(null);
    setTargetCandidate(null);
    setDialogState({ kind: "create" });
  };

  const openEditDialog = (property: SavedProperty) => {
    setFormState(formStateFromProperty(property));
    setFormError(null);
    setTargetCandidate(null);
    setDialogState({ kind: "edit", propertyID: property.id });
  };

  const openTargetDialog = (property: SavedProperty) => {
    setTargetCandidate(property);
    setFormError(null);
    setDialogState({ kind: "target", propertyID: property.id });
  };

  const handleSave = async () => {
    const validationError = validateFormState(formState);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Missing auth token");
      }

      const body = buildSavedPropertyInput(formState);
      const currentDialogState = dialogState;
      const mode = currentDialogState?.kind;
      let savedProperty: SavedProperty;
      if (currentDialogState?.kind === "edit") {
        savedProperty = await updateSavedProperty(
          currentDialogState.propertyID,
          body,
          token,
        );
      } else {
        savedProperty = await createSavedProperty(body, token);
      }
      const refreshedProperties = await refreshProperties();
      onReadinessChanged?.();

      if (formState.setAsTarget && savedProperty.id !== activePropertyID) {
        setTargetCandidate(
          refreshedProperties.find(
            (property) => property.id === savedProperty.id,
          ) ?? savedProperty,
        );
        setDialogState({ kind: "target", propertyID: savedProperty.id });
        pushToast({
          title: mode === "edit" ? "Property updated" : "Property added",
        });
        return;
      }

      setTargetCandidate(null);
      setDialogState(null);
      pushToast({
        title: mode === "edit" ? "Property updated" : "Property added",
      });
      if (mode === "create") {
        onOpenProperty?.(savedProperty.id);
      }
    } catch (error) {
      setFormError(getApiErrorMessage(error, "Unable to save property."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProperty) {
      return;
    }

    setSaving(true);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Missing auth token");
      }

      await deleteSavedProperty(selectedProperty.id, token);
      await refreshProperties();
      onReadinessChanged?.();
      setTargetCandidate(null);
      setDialogState(null);
      pushToast({ title: "Property deleted" });
    } catch (error) {
      pushToast({
        title: "Delete failed",
        description: getApiErrorMessage(error, "Try again in a moment."),
      });
    } finally {
      setSaving(false);
    }
  };

  const applyInlinePropertyChanges = (
    propertyID: string,
    changes: Partial<Pick<SavedProperty, "favorite" | "status">>,
  ) => {
    startTransition(() => {
      setProperties((current) =>
        current.map((property) =>
          property.id === propertyID ? { ...property, ...changes } : property,
        ),
      );
    });
  };

  const commitInlinePropertyUpdate = async ({
    changes,
    failureTitle,
    kind,
    property,
  }: {
    changes: Partial<Pick<SavedProperty, "favorite" | "status">>;
    failureTitle: string;
    kind: NonNullable<InlineActionState>["kind"];
    property: SavedProperty;
  }) => {
    const previous = {
      favorite: property.favorite,
      status: property.status,
    };

    applyInlinePropertyChanges(property.id, changes);
    setInlineAction({ kind, propertyID: property.id });

    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Missing auth token");
      }

      await updateSavedProperty(
        property.id,
        buildSavedPropertyInputFromProperty(property, changes),
        token,
      );
      await refreshProperties();
    } catch (error) {
      applyInlinePropertyChanges(property.id, previous);
      pushToast({
        title: failureTitle,
        description: getApiErrorMessage(error, "Try again in a moment."),
      });
    } finally {
      setInlineAction((current) =>
        current?.propertyID === property.id ? null : current,
      );
    }
  };

  const handleFavoriteToggle = async (property: SavedProperty) => {
    await commitInlinePropertyUpdate({
      changes: { favorite: !property.favorite },
      failureTitle: "Couldn't update favorite",
      kind: "favorite",
      property,
    });
  };

  const handleStatusChange = async (
    property: SavedProperty,
    status: SavedPropertyStatus,
  ) => {
    if (property.status === status) {
      return;
    }

    await commitInlinePropertyUpdate({
      changes: { status },
      failureTitle: "Couldn't update status",
      kind: "status",
      property,
    });
  };

  const handleConfirmSetAsTarget = async () => {
    if (!selectedProperty) {
      return;
    }

    const property = selectedProperty;
    setInlineAction({ kind: "target", propertyID: property.id });
    setSaving(true);

    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Missing auth token");
      }

      await setSavedPropertyAsReadinessTarget(property, activeTarget, token);
      await refreshProperties();
      onReadinessChanged?.();
      setTargetCandidate(null);
      setDialogState(null);
      pushToast({
        title: "Readiness target updated",
        description: `${property.address_line1} is now your active property target.`,
      });
    } catch (error) {
      pushToast({
        title: "Couldn't update target",
        description: getApiErrorMessage(error, "Try again in a moment."),
      });
    } finally {
      setSaving(false);
      setInlineAction((current) =>
        current?.propertyID === property.id ? null : current,
      );
    }
  };

  return (
    <>
      <section className="space-y-6">
        <PageHeader
          title="Save the homes you want to keep comparing."
          description="Track favorites, update where each home stands, and clear out the ones you have ruled out."
          actions={
            <Button className="rounded-pill" onClick={openCreateDialog}>
              <Plus className="h-4 w-4" />
              Add property
            </Button>
          }
        />

        {shouldShowPropertyFilters ? (
          <section className="surface-card bg-card p-4 sm:p-5">
            <div className="grid gap-4 md:grid-cols-[minmax(0,1.8fr)_220px_auto] md:items-end">
              <Input
                id="properties-search"
                label="Search your properties"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by address, city, or ZIP"
                value={query}
              />
              <Select
                items={STATUS_FILTER_ITEMS}
                label="Status"
                onValueChange={setStatusFilter}
                value={statusFilter}
              />
              <Checkbox
                checked={favoriteOnly}
                description="Show only homes you have starred."
                label="Favorites only"
                onCheckedChange={setFavoriteOnly}
              />
            </div>
          </section>
        ) : null}

        {loading ? <PropertiesListSkeleton /> : null}

        {!loading && loadError ? (
          <section className="surface-card border-destructive/20 bg-card p-6">
            <p className="text-sm font-medium text-foreground">
              We couldn&apos;t load your properties
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{loadError}</p>
          </section>
        ) : null}

        {!loading && !loadError && properties.length === 0 ? (
          <section className="surface-panel bg-card p-6 sm:p-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="eyebrow-label">No matches yet</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-[-0.03em] text-foreground">
                {hasActivePropertyFilters
                  ? "No saved homes match those filters."
                  : "Start with the first home you want to keep an eye on."}
              </h2>
              <p className="body-muted mt-3">
                {hasActivePropertyFilters
                  ? "Try a broader search or clear a filter to see the rest of your list."
                  : "Add an address, mark a favorite, and keep the homes you are actively comparing in one place."}
              </p>
              {!hasActivePropertyFilters ? (
                <Button
                  className="mt-5 rounded-pill"
                  onClick={openCreateDialog}
                >
                  <Plus className="h-4 w-4" />
                  Add your first property
                </Button>
              ) : null}
            </div>
          </section>
        ) : null}

        {!loading && !loadError && properties.length > 0 ? (
          <section className="space-y-3">
            {properties.map((property) => {
              const propertyUpdating = inlineAction?.propertyID === property.id;
              const targetUpdating =
                inlineAction?.kind === "target" &&
                inlineAction.propertyID === property.id;
              const isActiveTarget = property.id === activePropertyID;
              const planningPreview = buildPropertyPlanningPreview(
                property,
                snapshot,
                readinessPreviews[property.id],
                cashPreviews[property.id],
                activeTarget,
              );
              const visualClasses = getSavedPropertyVisualTokenClasses(
                property.visual_token,
              );

              return (
                <article
                  key={property.id}
                  className={cn(
                    "surface-card p-4 sm:p-[17px]",
                    visualClasses.card,
                  )}
                  data-visual-token={property.visual_token}
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0 space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            aria-label={
                              property.favorite
                                ? "Remove favorite"
                                : "Mark as favorite"
                            }
                            aria-pressed={property.favorite}
                            className={cn(
                              "-mx-0.5 shrink-0 rounded-pill border-0 bg-transparent p-0 transition-colors",
                              property.favorite
                                ? "text-primary-dark"
                                : "text-muted-foreground hover:text-primary-dark",
                              propertyUpdating &&
                                "pointer-events-none cursor-not-allowed opacity-50",
                            )}
                            data-slot="interactive-control"
                            disabled={propertyUpdating}
                            onClick={() => void handleFavoriteToggle(property)}
                            title={
                              property.favorite
                                ? "Remove favorite"
                                : "Mark as favorite"
                            }
                            type="button"
                          >
                            <Star
                              className={cn(
                                "h-[1.3rem] w-[1.3rem]",
                                property.favorite && "fill-current",
                              )}
                            />
                          </button>
                          {onOpenProperty ? (
                            <button
                              className="min-w-0 rounded-sm text-left text-lg font-semibold tracking-[-0.02em] text-foreground transition-colors hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              onClick={() => onOpenProperty(property.id)}
                              type="button"
                            >
                              {property.address_line1}
                            </button>
                          ) : (
                            <h2 className="text-lg font-semibold tracking-[-0.02em] text-foreground">
                              {property.address_line1}
                            </h2>
                          )}
                          {isActiveTarget ? (
                            <Badge variant="primary">Active target</Badge>
                          ) : (
                            <Button
                              disabled={propertyUpdating}
                              loading={targetUpdating}
                              onClick={() => openTargetDialog(property)}
                              size="sm"
                              variant="outline"
                            >
                              Set as target
                            </Button>
                          )}
                        </div>

                        <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          <span>{buildPropertyLocation(property)}</span>
                        </p>

                        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                          <span className="text-base font-bold tabular-nums text-foreground">
                            {formatCompactCurrency(property.list_price)}
                          </span>
                          <span className="text-muted-foreground">
                            {formatSavedPropertyType(property.property_type)}
                          </span>
                          <span className="text-muted-foreground">
                            {property.view_count} view
                            {property.view_count === 1 ? "" : "s"}
                          </span>
                        </div>

                        {property.notes ? (
                          <p className="text-sm leading-6 text-muted-foreground">
                            {property.notes}
                          </p>
                        ) : null}

                        {property.listing_url ? (
                          <a
                            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                            href={property.listing_url}
                            rel="noreferrer"
                            target="_blank"
                          >
                            Open listing
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        ) : null}
                      </div>

                      <div className="flex w-full flex-col gap-2 sm:w-auto lg:w-[240px]">
                        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                          <Button
                            onClick={() => openEditDialog(property)}
                            size="sm"
                            variant="outline"
                          >
                            <Pencil className="h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            onClick={() =>
                              setDialogState({
                                kind: "delete",
                                propertyID: property.id,
                              })
                            }
                            size="sm"
                            variant="ghost"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                          <Select
                            className="w-full sm:w-[180px]"
                            disabled={propertyUpdating}
                            items={SAVED_PROPERTY_STATUS_OPTIONS}
                            onValueChange={(value) =>
                              void handleStatusChange(
                                property,
                                value as SavedPropertyStatus,
                              )
                            }
                            triggerClassName="min-h-9 rounded-pill border-border/70 bg-background px-3 text-left shadow-none"
                            value={property.status}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-2 sm:grid-cols-3">
                      <PropertyPreviewMetric
                        label="Readiness"
                        value={planningPreview.readinessValue}
                        detail={planningPreview.readinessDetail}
                        valueClassName={planningPreview.readinessValueClassName}
                      />
                      <PropertyPreviewMetric
                        label={planningPreview.monthlyLabel}
                        value={formatCurrency(planningPreview.monthlyPayment)}
                        detail={planningPreview.monthlyDetail}
                        tone={planningPreview.monthlyTone}
                      />
                      <PropertyPreviewMetric
                        label="Cash plan"
                        value={
                          planningPreview.cashToClose === null
                            ? "Previewing"
                            : formatCurrency(planningPreview.cashToClose)
                        }
                        detail={planningPreview.cashPlanDetail}
                      />
                    </div>

                    <div className="flex flex-col gap-3 border-t border-border/70 pt-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p
                          className={cn(
                            "text-sm font-semibold",
                            planningPreview.fitClassName,
                          )}
                        >
                          {planningPreview.fitLabel}
                        </p>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                          {planningPreview.fitDescription}
                        </p>
                      </div>
                      {onOpenProperty ? (
                        <Button
                          className="w-full rounded-pill sm:w-auto"
                          onClick={() => onOpenProperty(property.id)}
                        >
                          View details
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })}
          </section>
        ) : null}
      </section>

      <Dialog
        open={dialogState?.kind === "create" || dialogState?.kind === "edit"}
        onOpenChange={(open) => !open && setDialogState(null)}
      >
        <DialogContent
          description="Save the basics now. You can keep refining details as you compare homes."
          title={
            dialogState?.kind === "edit" ? "Edit property" : "Add property"
          }
        >
          <PropertyFormFields
            formState={formState}
            setFormState={setFormState}
          />

          {formError ? (
            <p
              className="mt-4 text-sm font-medium text-destructive"
              role="alert"
            >
              {formError}
            </p>
          ) : null}

          <div className="mt-6 flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogState(null)}>
              Cancel
            </Button>
            <Button loading={saving} onClick={handleSave}>
              {dialogState?.kind === "edit" ? "Save changes" : "Add property"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={dialogState?.kind === "target"}
        onOpenChange={(open) => {
          if (!open) {
            setTargetCandidate(null);
            setDialogState(null);
          }
        }}
      >
        <DialogContent
          description="Sill will recalculate readiness and property scenarios around this home. You can change it again later."
          title="Set this as your readiness target?"
        >
          <div className="space-y-4">
            <div className="rounded-2xl bg-muted/40 px-4 py-3">
              <p className="text-sm font-semibold text-foreground">
                {selectedProperty?.address_line1 ?? "Saved property"}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {selectedProperty
                  ? buildPropertyLocation(selectedProperty)
                  : "We couldn't find this property in your current list."}
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setTargetCandidate(null);
                  setDialogState(null);
                }}
              >
                Keep current target
              </Button>
              <Button
                loading={saving || inlineAction?.kind === "target"}
                onClick={() => void handleConfirmSetAsTarget()}
              >
                Set as target
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={dialogState?.kind === "delete"}
        onOpenChange={(open) => !open && setDialogState(null)}
      >
        <DialogContent
          description="This removes the property from your saved list. You can add it again later, but we will not keep notes or status history for this copy."
          title="Delete this property?"
        >
          <div className="space-y-4">
            <div className="rounded-2xl bg-muted/40 px-4 py-3">
              <p className="text-sm font-semibold text-foreground">
                {selectedProperty?.address_line1 ?? "Saved property"}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {selectedProperty
                  ? buildPropertyLocation(selectedProperty)
                  : "We couldn&apos;t find this property in your current list."}
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogState(null)}>
                Keep property
              </Button>
              <Button
                loading={saving}
                onClick={handleDelete}
                variant="destructive"
              >
                Delete property
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function buildPropertyLocation(property: SavedProperty) {
  const locationBits = [
    property.city,
    property.state,
    property.postal_code,
  ].filter((value): value is string => Boolean(value));
  if (locationBits.length === 0) {
    return formatSavedPropertyType(property.property_type);
  }
  return `${locationBits.join(", ")} · ${formatSavedPropertyType(property.property_type)}`;
}

function PropertyPreviewMetric({
  detail,
  label,
  tone = "neutral",
  value,
  valueClassName,
}: {
  detail: string;
  label: string;
  tone?: "neutral" | "strong" | "warning";
  value: string;
  valueClassName?: string | null;
}) {
  return (
    <div className="rounded-2xl bg-background/70 px-3 py-3">
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </p>
      <p
        className={cn(
          "mt-1 text-lg font-semibold tabular-nums text-foreground",
          tone === "strong" && "text-primary-dark",
          tone === "warning" && "text-warning-foreground",
          valueClassName,
        )}
      >
        {value}
      </p>
      <p className="mt-1 text-xs leading-5 text-muted-foreground">{detail}</p>
    </div>
  );
}

function buildCashPreviewSnapshotKey(snapshot: DashboardSnapshot) {
  return [
    snapshot.creditBucket ?? "",
    snapshot.downPayment?.amount ?? "",
    snapshot.downPayment?.percent ?? "",
    snapshot.hasBonusIncome ? "bonus" : "",
    snapshot.hasCommissionIncome ? "commission" : "",
    snapshot.hasSecondJob ? "second_job" : "",
    snapshot.householdIncome,
    snapshot.isFirstTimeBuyer ? "first_time" : "",
    snapshot.isSelfEmployed ? "self_employed" : "",
    snapshot.monthlyDebts,
    snapshot.savings,
  ].join("|");
}

function buildCashPreviewTargetKey(target: ReadinessTarget | null) {
  if (!target) {
    return "none";
  }

  return [
    target.id,
    target.created_at,
    target.kind,
    target.property_id ?? "",
    target.applied_dpa_program_id ?? "",
    target.deal.preferred_product ?? "",
    target.deal.preferred_term_mo ?? "",
    target.deal.property.occupancy ?? "",
    target.deal.property.units ?? "",
    target.deal.savings_goal_amount ?? "",
    target.deal.savings_goal_pct ?? "",
    target.deal.seller_lender_credits_cents ?? "",
  ].join("|");
}

function buildCashPreviewInputKey(
  snapshot: DashboardSnapshot,
  target: ReadinessTarget | null,
) {
  return `${buildCashPreviewSnapshotKey(snapshot)}|${buildCashPreviewTargetKey(target)}`;
}

function buildPropertyCashEstimateRequest(
  property: SavedProperty,
  snapshot: DashboardSnapshot,
  activeTarget: ReadinessTarget | null,
): EstimateRequest {
  const downPaymentPlan = resolveDownPaymentPlan(
    property,
    snapshot,
    activeTarget,
  );
  return {
    as_of: new Date().toISOString(),
    borrower: {
      credit_bucket: snapshot.creditBucket ?? undefined,
      gross_annual_income: dollarsToCents(snapshot.householdIncome),
      has_bonus_income: Boolean(snapshot.hasBonusIncome),
      has_commission_income: Boolean(snapshot.hasCommissionIncome),
      has_second_job: Boolean(snapshot.hasSecondJob),
      is_self_employed: Boolean(snapshot.isSelfEmployed),
      liquid_assets: dollarsToCents(snapshot.savings),
      monthly_debts: dollarsToCents(snapshot.monthlyDebts),
    },
    financing: {
      ami_eligible: false,
      down_payment_amount: dollarsToCents(downPaymentPlan.amount),
      down_payment_percent: percentToDecimal(downPaymentPlan.percent),
      down_payment_strategy: "fixed_percent",
      first_time_buyer: snapshot.isFirstTimeBuyer,
      loan_term_months: activeTarget?.deal.preferred_term_mo ?? 360,
      product: activeTarget?.deal.preferred_product ?? "conventional",
      purpose: "purchase",
      subordinate_financing: false,
      veteran_eligible: false,
    },
    mode: "enriched",
    property: {
      estimated_taxes:
        property.estimated_property_taxes_monthly_cents ?? undefined,
      estimated_insurance:
        property.estimated_homeowners_insurance_monthly_cents ?? undefined,
      hoa: property.estimated_hoa_monthly_cents ?? undefined,
      location_code: property.postal_code ?? property.state ?? "",
      occupancy: activeTarget?.deal.property.occupancy ?? "primary",
      property_type: property.property_type,
      target_price: dollarsToCents(property.list_price),
      units: activeTarget?.deal.property.units ?? 1,
    },
    requested_products: [
      activeTarget?.deal.preferred_product ?? "conventional",
    ],
  };
}

function buildPropertyCashRequest(
  estimate: EstimateRequest,
  activeTarget: ReadinessTarget | null,
) {
  const appliedDPAProgramID = activeTarget?.applied_dpa_program_id?.trim();
  const sellerLenderCreditsCents =
    activeTarget?.deal.seller_lender_credits_cents ?? 0;
  return {
    estimate,
    ...(appliedDPAProgramID
      ? { applied_dpa_program_id: appliedDPAProgramID }
      : {}),
    ...(sellerLenderCreditsCents > 0
      ? { seller_lender_credits_cents: sellerLenderCreditsCents }
      : {}),
  };
}

function buildPropertyPlanningPreview(
  property: SavedProperty,
  snapshot: DashboardSnapshot,
  readinessPreview: ReadinessPreviewState[string] | undefined,
  cashPreview: CashPreviewState[string],
  activeTarget: ReadinessTarget | null,
) {
  const downPaymentPlan = resolveDownPaymentPlan(
    property,
    snapshot,
    activeTarget,
  );
  const downPaymentPercent = downPaymentPlan.percent;
  const downPaymentAmount = downPaymentPlan.amount;
  const loanAmount = Math.max(0, property.list_price - downPaymentAmount);
  const principalInterest = amortizedMonthlyPayment(
    loanAmount,
    DEFAULT_RATE_PERCENT / 100,
    360,
  );
  const propertyTaxes =
    centsToDollarsOrNull(property.estimated_property_taxes_monthly_cents) ??
    Math.round((property.list_price * PREVIEW_PROPERTY_TAX_RATE) / 12);
  const insurance =
    centsToDollarsOrNull(
      property.estimated_homeowners_insurance_monthly_cents,
    ) ?? Math.round((property.list_price * PREVIEW_INSURANCE_RATE) / 12);
  const hoa = centsToDollarsOrNull(property.estimated_hoa_monthly_cents) ?? 0;
  const mortgageInsurance =
    !downPaymentPlan.isNoMortgage && downPaymentPercent < 20
      ? Math.round((loanAmount * PREVIEW_PMI_RATE) / 12)
      : 0;
  const monthlyPayment =
    principalInterest + propertyTaxes + insurance + mortgageInsurance + hoa;
  const monthlyDelta = monthlyPayment - snapshot.monthlyBudget;
  // Saved-property preview math is in dollars here. Backend previews return
  // cents and are converted below before display.
  const fallbackCashToClose = Math.round(
    downPaymentAmount + property.list_price * PREVIEW_CLOSING_COST_RATE,
  );
  const cashToClose =
    cashPreview === undefined
      ? null
      : typeof cashPreview?.totalCashToClose === "number"
        ? Math.round(cashPreview.totalCashToClose / 100)
        : fallbackCashToClose;
  const planUnderfunded =
    readinessPreview?.plan_viability?.executable === false;
  const fit = resolvePropertyFit(
    property,
    snapshot,
    monthlyPayment,
    planUnderfunded,
  );
  const readinessScore = readinessPreview?.score ?? null;
  const readinessDisplay =
    readinessScore === null ? null : getReadinessScoreDisplay(readinessScore);
  const readinessIsPending = readinessPreview === undefined;
  const readinessValue = readinessIsPending
    ? "Previewing"
    : typeof readinessScore === "number"
      ? `${readinessScore}/100`
      : "—";
  const readinessDetail = readinessIsPending
    ? "Score for this property target"
    : (readinessPreview?.label ?? "Readiness unavailable");
  const readinessValueClassName =
    readinessDisplay?.textClassName ??
    (readinessPreview?.label === "Not yet qualified" ||
    readinessPreview?.label === "Plan underfunded"
      ? "text-destructive"
      : null);

  return {
    cashToClose,
    downPaymentPercent,
    fitClassName: fit.className,
    fitDescription: fit.description,
    fitLabel: fit.label,
    cashPlanDetail: downPaymentPlan.isNoMortgage
      ? "Estimated cash to close"
      : cashPreview
        ? "Estimated cash to close"
        : cashPreview === undefined
          ? "Previewing backend cash estimate"
          : `${formatPercent(downPaymentPercent, 1)} down + fallback closing costs`,
    monthlyDetail: downPaymentPlan.isNoMortgage
      ? "Taxes, insurance, and HOA only"
      : buildMonthlyDetail(monthlyDelta, snapshot.monthlyBudget),
    monthlyLabel: downPaymentPlan.isNoMortgage
      ? "Monthly costs"
      : "Monthly PITI",
    monthlyPayment,
    monthlyTone: monthlyDelta <= 0 ? ("strong" as const) : ("warning" as const),
    readinessDetail,
    readinessScore,
    readinessValue,
    readinessValueClassName,
  };
}

function resolveDownPaymentPlan(
  property: SavedProperty,
  snapshot: DashboardSnapshot,
  activeTarget: ReadinessTarget | null,
) {
  const explicitPct = activeTarget?.deal.savings_goal_pct;
  if (typeof explicitPct === "number" && Number.isFinite(explicitPct)) {
    const percent = clamp(explicitPct * 100, 0, 100);
    const amount = Math.round(property.list_price * (percent / 100));
    return {
      amount,
      isNoMortgage: amount >= property.list_price || percent >= 100,
      percent,
    };
  }

  const explicitAmount = activeTarget?.deal.savings_goal_amount;
  if (
    typeof explicitAmount === "number" &&
    Number.isFinite(explicitAmount) &&
    explicitAmount > 0
  ) {
    const amount = Math.min(Math.round(explicitAmount), property.list_price);
    const percent =
      property.list_price > 0
        ? clamp((amount / property.list_price) * 100, 0, 100)
        : 0;
    return {
      amount,
      isNoMortgage: amount >= property.list_price || percent >= 100,
      percent,
    };
  }

  const inferredDownPaymentPercent = Math.round(
    (snapshot.savings / property.list_price) * 100,
  );
  const percent = clamp(
    snapshot.downPayment?.percent || inferredDownPaymentPercent || 10,
    3,
    40,
  );
  return {
    amount: Math.round(property.list_price * (percent / 100)),
    isNoMortgage: false,
    percent,
  };
}

function buildMonthlyDetail(monthlyDelta: number, monthlyBudget: number) {
  if (monthlyBudget <= 0) {
    return "Add a comfort target to compare payment fit";
  }
  if (monthlyDelta <= 0) {
    return `${formatCurrency(Math.abs(monthlyDelta))}/mo under comfort`;
  }
  return `${formatCurrency(monthlyDelta)}/mo over comfort`;
}

function getReadinessScoreDisplay(score: number) {
  return getReadinessScoreAppearance(score);
}

function amortizedMonthlyPayment(
  principal: number,
  annualRate: number,
  months: number,
) {
  const monthlyRate = annualRate / 12;
  if (monthlyRate === 0) {
    return Math.round(principal / months);
  }
  const factor = (1 + monthlyRate) ** months;
  return Math.round((principal * monthlyRate * factor) / (factor - 1));
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function centsToDollarsOrNull(value: number | null | undefined) {
  return typeof value === "number" ? value / 100 : null;
}

function toTrackedProperty(
  property: SavedProperty,
  readinessPreview?: SavedPropertyReadinessPreview | null,
): TrackedProperty {
  return {
    id: property.id,
    address: property.address_line1,
    cityMeta: buildPropertyLocation(property),
    listPrice: property.list_price,
    monthlyPayment: null,
    readinessLabel: readinessPreview?.label ?? null,
    readinessScore: readinessPreview?.score ?? null,
    visualToken: property.visual_token,
  };
}
